import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { ArrowLeft, Plus, Trash2, Save, Send, AlertTriangle, Search } from 'lucide-react';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { toast } from '../../components/ui/sonner';

const CURRENCIES = ['USD', 'GBP', 'EUR', 'INR'];
const ENTITIES = ['Eyvo USA', 'Eyvo India'];

const fmt = (n, c) => {
  try { return new Intl.NumberFormat('en-GB', { style: 'currency', currency: c || 'USD' }).format(n || 0); }
  catch { return `${c} ${Number(n || 0).toFixed(2)}`; }
};

const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const blankLine = () => ({
  key: `l${Math.random().toString(36).slice(2, 10)}`,
  description: '', quantity: '1', unit_price: '', tax_amount: '', account_code: '',
});

const addDays = (iso, days) => {
  const d = new Date(iso);
  d.setDate(d.getDate() + days);
  return d.toISOString().slice(0, 10);
};

const today = () => new Date().toISOString().slice(0, 10);

export default function InvoiceForm() {
  const { invoiceId } = useParams();
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const isEdit = Boolean(invoiceId);

  // One idempotency key per mounted form. A double-clicked Save is one
  // submission, not two invoices — and the server only knows that if we say so.
  const requestId = useRef(`inv-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`);

  const [loading, setLoading]   = useState(isEdit);
  const [saving, setSaving]     = useState('');
  const [loadError, setError]   = useState('');

  const [clients, setClients]       = useState([]);
  const [clientQuery, setQuery]     = useState('');
  const [showPicker, setShowPicker] = useState(false);
  const [client, setClient]         = useState(null);   // selected Pulse client

  const [contactName, setContactName] = useState('');
  const [entity, setEntity]           = useState('Eyvo USA');
  const [currency, setCurrency]       = useState('USD');
  const [invoiceDate, setInvoiceDate] = useState(today());
  const [dueDate, setDueDate]         = useState(addDays(today(), 30));
  const [reference, setReference]     = useState('');
  const [notes, setNotes]             = useState('');
  const [notesInternal, setInternal]  = useState('');
  const [lines, setLines]             = useState([blankLine()]);

  // ── Load the client list for the picker ───────────────────────────────────
  useEffect(() => {
    api.get('/api/clients')
      .then(r => setClients(r.data?.clients || []))
      .catch(() => setClients([]));   // Pulse unconfigured — free-text still works
  }, []);

  // ── Load an existing draft ────────────────────────────────────────────────
  const load = useCallback(() => {
    if (!isEdit) return;
    setLoading(true);
    api.get(`/api/invoices/${invoiceId}`)
      .then(r => {
        const inv = r.data.invoice;
        if (inv.status !== 'draft') {
          setError(
            `Invoice ${inv.invoice_number} has been issued. Its lines and totals ` +
            `are closed — void it and re-raise, or issue a credit note.`
          );
          return;
        }
        setContactName(inv.client_name || '');
        setEntity(inv.entity || 'Eyvo USA');
        setCurrency(inv.currency_code || 'USD');
        setInvoiceDate(inv.issue_date || today());
        setDueDate(inv.due_date || today());
        setReference(inv.reference_number || '');
        setNotes(inv.notes_external || '');
        setInternal(inv.notes_internal || '');
        if (inv.client_id) setClient({ client_id: inv.client_id, legal_name: inv.client_name });
        setLines((r.data.line_items || []).map(ln => ({
          key: ln.line_id || `l${ln.line_number}`,
          description:  ln.description_snapshot || '',
          quantity:     String(ln.quantity ?? 1),
          unit_price:   String(ln.unit_price ?? ''),
          tax_amount:   ln.line_tax_amount ? String(ln.line_tax_amount) : '',
          account_code: ln.nominal_ledger_code_placeholder || '',
        })) || [blankLine()]);
      })
      .catch(e => setError(e.response?.data?.detail || 'Invoice not found'))
      .finally(() => setLoading(false));
  }, [invoiceId, isEdit]);

  useEffect(() => { load(); }, [load]);

  // ── Totals, computed exactly as the server does ───────────────────────────
  // Each line rounds once and the rounded amounts are summed, so what the user
  // sees adds up the same way the invoice will.
  const totals = useMemo(() => {
    const subtotal = lines.reduce(
      (sum, ln) => sum + round2((Number(ln.quantity) || 0) * (Number(ln.unit_price) || 0)), 0);
    const tax = lines.reduce((sum, ln) => sum + round2(Number(ln.tax_amount) || 0), 0);
    return { subtotal: round2(subtotal), tax: round2(tax), total: round2(subtotal + tax) };
  }, [lines]);

  const filteredClients = useMemo(() => {
    const q = clientQuery.trim().toLowerCase();
    const rows = q
      ? clients.filter(c =>
          (c.legal_name || '').toLowerCase().includes(q) ||
          (c.name || '').toLowerCase().includes(q) ||
          (c.client_code || '').toLowerCase().includes(q) ||
          (c.billing_email || '').toLowerCase().includes(q))
      : clients;
    return rows.slice(0, 50);
  }, [clients, clientQuery]);

  const setLine = (key, patch) =>
    setLines(prev => prev.map(ln => (ln.key === key ? { ...ln, ...patch } : ln)));

  const removeLine = (key) =>
    setLines(prev => (prev.length === 1 ? [blankLine()] : prev.filter(ln => ln.key !== key)));

  const pickClient = (c) => {
    setClient(c);
    setContactName(c.legal_name || c.name || '');
    if (c.currency_code) setCurrency(c.currency_code);
    if (c.payment_terms_days) setDueDate(addDays(invoiceDate, Number(c.payment_terms_days)));
    setShowPicker(false);
    setQuery('');
  };

  const payload = () => ({
    contact_name: contactName.trim(),
    entity,
    currency_code: currency,
    invoice_date: invoiceDate,
    due_date: dueDate,
    reference: reference || undefined,
    notes_external: notes || undefined,
    notes_internal: notesInternal || undefined,
    client_id: client?.client_id || undefined,
    client_request_id: requestId.current,
    lines: lines
      .filter(ln => ln.description.trim())
      .map(ln => ({
        description: ln.description.trim(),
        quantity: Number(ln.quantity) || 0,
        unit_price: Number(ln.unit_price) || 0,
        tax_amount: Number(ln.tax_amount) || 0,
        account_code: ln.account_code || undefined,
      })),
  });

  const validate = () => {
    if (!contactName.trim()) return 'Choose a customer, or type a name.';
    const usable = lines.filter(ln => ln.description.trim());
    if (usable.length === 0) return 'An invoice needs at least one line.';
    if (usable.some(ln => !(Number(ln.quantity) > 0))) return 'Every line needs a quantity above zero.';
    if (totals.total <= 0) return 'An invoice total must be positive.';
    if (dueDate < invoiceDate) return 'The due date cannot be before the invoice date.';
    return '';
  };

  const save = async (thenIssue) => {
    const problem = validate();
    if (problem) { toast.error(problem); return; }

    setSaving(thenIssue ? 'issuing' : 'saving');
    try {
      let id = invoiceId;
      if (isEdit) {
        await api.put(`/api/invoices/${invoiceId}/draft`, payload());
      } else {
        const res = await api.post('/api/invoices', payload());
        id = res.data.invoice_id;
        if (res.data.duplicate) toast.info('This invoice was already created — opening it.');
      }

      if (thenIssue) {
        const issued = await api.post(`/api/invoices/${id}/issue`);
        toast.success(
          issued.data.already_issued
            ? `Invoice ${issued.data.invoice_number} was already issued`
            : `Invoice ${issued.data.invoice_number} issued`
        );
      } else {
        toast.success('Draft saved');
      }
      navigate(`/invoices/${id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save this invoice');
    } finally {
      setSaving('');
    }
  };

  if (!canWrite()) {
    return <div className="p-6 text-slate-500" data-testid="invoice-form-readonly">
      You do not have permission to raise invoices.
    </div>;
  }

  if (loading) {
    return <div className="p-6"><div className="h-4 w-48 bg-slate-100 rounded animate-pulse" /></div>;
  }

  if (loadError) {
    return (
      <div className="p-6 max-w-2xl" data-testid="invoice-form-error">
        <Link to="/invoices" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4">
          <ArrowLeft size={15} /> Back to Invoices
        </Link>
        <div className="flex gap-3 bg-amber-50 border border-amber-200 rounded-md p-4">
          <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
          <p className="text-sm text-amber-900">{loadError}</p>
        </div>
      </div>
    );
  }

  const busy = Boolean(saving);

  return (
    <div className="p-6 fade-in max-w-5xl" data-testid="invoice-form-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <Link to="/invoices" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
            <ArrowLeft size={15} /> Back to Invoices
          </Link>
          <h1 className="text-2xl font-bold text-slate-900 mt-1" style={{ fontFamily: 'Chivo, sans-serif' }}>
            {isEdit ? 'Edit Draft Invoice' : 'New Invoice'}
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            A draft has no invoice number and no ledger effect. Issuing it assigns
            the number and posts it.
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={() => save(false)} disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 border border-slate-200 text-slate-700 text-sm font-medium rounded-md hover:bg-slate-50 disabled:opacity-50"
            data-testid="save-draft-button">
            <Save size={14} /> {saving === 'saving' ? 'Saving…' : 'Save Draft'}
          </button>
          <button onClick={() => save(true)} disabled={busy}
            className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-900 text-white text-sm font-semibold rounded-md hover:bg-indigo-800 disabled:opacity-50"
            data-testid="save-and-issue-button">
            <Send size={14} /> {saving === 'issuing' ? 'Issuing…' : 'Save & Issue'}
          </button>
        </div>
      </div>

      {/* ── Customer and dates ───────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-lg p-5 mb-4">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">Customer</label>
            <div className="relative">
              <input
                type="text"
                value={contactName}
                onChange={e => { setContactName(e.target.value); setClient(null); }}
                onFocus={() => setShowPicker(true)}
                placeholder="Search clients, or type a name"
                className="w-full border border-slate-200 rounded px-3 py-2 text-sm pr-8"
                data-testid="invoice-customer-input"
              />
              <Search size={14} className="absolute right-3 top-2.5 text-slate-400 pointer-events-none" />

              {showPicker && clients.length > 0 && (
                <div className="absolute z-20 mt-1 w-full max-h-64 overflow-y-auto bg-white border border-slate-200 rounded-md shadow-lg"
                     data-testid="invoice-client-picker">
                  <div className="sticky top-0 bg-white border-b border-slate-100 p-2">
                    <input
                      type="text" autoFocus value={clientQuery}
                      onChange={e => setQuery(e.target.value)}
                      placeholder="Filter…"
                      className="w-full border border-slate-200 rounded px-2 py-1 text-xs"
                      data-testid="invoice-client-search"
                    />
                  </div>
                  {filteredClients.length === 0 ? (
                    <p className="px-3 py-3 text-xs text-slate-400">No matching clients</p>
                  ) : filteredClients.map(c => (
                    <button
                      key={c.client_id} type="button" onClick={() => pickClient(c)}
                      className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-50 last:border-0"
                      data-testid={`invoice-client-option-${c.client_id}`}
                    >
                      <span className="block text-sm text-slate-900">{c.legal_name || c.name}</span>
                      <span className="block text-xs text-slate-400">
                        {c.client_code}{c.billing_email ? ` · ${c.billing_email}` : ''}
                      </span>
                    </button>
                  ))}
                  <button type="button" onClick={() => setShowPicker(false)}
                    className="w-full px-3 py-2 text-xs text-slate-500 hover:bg-slate-50">
                    Close
                  </button>
                </div>
              )}
            </div>
            {client ? (
              <p className="text-xs text-emerald-600 mt-1" data-testid="invoice-client-linked">
                Linked to client {client.client_id} — their balance and history will include this invoice.
              </p>
            ) : contactName.trim() ? (
              <p className="text-xs text-amber-600 mt-1" data-testid="invoice-client-unlinked">
                Not linked to a client record. This invoice will be matched by name only.
              </p>
            ) : null}
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Invoice Date</label>
              <input type="date" value={invoiceDate}
                onChange={e => setInvoiceDate(e.target.value)}
                className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
                data-testid="invoice-date-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Due Date</label>
              <input type="date" value={dueDate} min={invoiceDate}
                onChange={e => setDueDate(e.target.value)}
                className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
                data-testid="invoice-due-date-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Currency</label>
              <select value={currency} onChange={e => setCurrency(e.target.value)}
                className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
                data-testid="invoice-currency-select">
                {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1">Entity</label>
              <select value={entity} onChange={e => setEntity(e.target.value)}
                className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
                data-testid="invoice-entity-select">
                {ENTITIES.map(e => <option key={e} value={e}>{e}</option>)}
              </select>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-xs font-medium text-slate-500 mb-1">Reference / PO Number</label>
          <input type="text" value={reference} onChange={e => setReference(e.target.value)}
            placeholder="e.g. PO-12345"
            className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
            data-testid="invoice-reference-input" />
        </div>
      </div>

      {/* ── Lines ────────────────────────────────────────────────────────── */}
      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden mb-4">
        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 bg-slate-50">
          <p className="text-sm font-semibold text-slate-800">Lines</p>
          <button type="button" onClick={() => setLines(prev => [...prev, blankLine()])}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-slate-200 bg-white text-slate-700 text-xs font-medium rounded-md hover:bg-slate-50"
            data-testid="add-line-button">
            <Plus size={13} /> Add Line
          </button>
        </div>

        <table className="w-full text-sm" data-testid="invoice-lines-table">
          <thead>
            <tr className="border-b border-slate-200 text-xs text-slate-500">
              <th className="text-left font-medium px-4 py-2 w-[40%]">Description</th>
              <th className="text-right font-medium px-3 py-2 w-24">Qty</th>
              <th className="text-right font-medium px-3 py-2 w-32">Rate</th>
              <th className="text-right font-medium px-3 py-2 w-28">Tax</th>
              <th className="text-left font-medium px-3 py-2 w-28">Account</th>
              <th className="text-right font-medium px-3 py-2 w-32">Amount</th>
              <th className="w-10" />
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {lines.map((ln, idx) => (
              <tr key={ln.key} data-testid={`invoice-line-row-${idx}`}>
                <td className="px-4 py-2">
                  <input type="text" value={ln.description}
                    onChange={e => setLine(ln.key, { description: e.target.value })}
                    placeholder="What is being billed"
                    className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm"
                    data-testid={`line-description-${idx}`} />
                </td>
                <td className="px-3 py-2">
                  <input type="number" min="0" step="0.0001" value={ln.quantity}
                    onChange={e => setLine(ln.key, { quantity: e.target.value })}
                    className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm text-right"
                    data-testid={`line-quantity-${idx}`} />
                </td>
                <td className="px-3 py-2">
                  <input type="number" min="0" step="0.01" value={ln.unit_price}
                    onChange={e => setLine(ln.key, { unit_price: e.target.value })}
                    placeholder="0.00"
                    className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm text-right"
                    data-testid={`line-unit-price-${idx}`} />
                </td>
                <td className="px-3 py-2">
                  <input type="number" min="0" step="0.01" value={ln.tax_amount}
                    onChange={e => setLine(ln.key, { tax_amount: e.target.value })}
                    placeholder="0.00"
                    className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm text-right"
                    data-testid={`line-tax-${idx}`} />
                </td>
                <td className="px-3 py-2">
                  <input type="text" value={ln.account_code}
                    onChange={e => setLine(ln.key, { account_code: e.target.value })}
                    placeholder="4000"
                    className="w-full border border-slate-200 rounded px-2 py-1.5 text-sm font-mono"
                    data-testid={`line-account-${idx}`} />
                </td>
                <td className="px-3 py-2 text-right font-mono text-slate-900"
                    data-testid={`line-amount-${idx}`}>
                  {fmt(round2((Number(ln.quantity) || 0) * (Number(ln.unit_price) || 0)), currency)}
                </td>
                <td className="px-2 py-2">
                  <button type="button" onClick={() => removeLine(ln.key)}
                    className="text-slate-300 hover:text-red-500"
                    data-testid={`remove-line-${idx}`} aria-label="Remove line">
                    <Trash2 size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="flex justify-end border-t border-slate-200 bg-slate-50 px-5 py-3">
          <dl className="text-sm w-64 space-y-1" data-testid="invoice-totals">
            <div className="flex justify-between text-slate-600">
              <dt>Subtotal</dt>
              <dd className="font-mono" data-testid="invoice-subtotal">{fmt(totals.subtotal, currency)}</dd>
            </div>
            <div className="flex justify-between text-slate-600">
              <dt>Tax</dt>
              <dd className="font-mono" data-testid="invoice-tax">{fmt(totals.tax, currency)}</dd>
            </div>
            <div className="flex justify-between border-t border-slate-200 pt-1 text-slate-900 font-semibold">
              <dt>Total</dt>
              <dd className="font-mono" data-testid="invoice-total">{fmt(totals.total, currency)}</dd>
            </div>
          </dl>
        </div>
      </div>

      {/* ── Notes ────────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <label className="block text-xs font-medium text-slate-500 mb-1">
            Notes for the customer
          </label>
          <textarea rows={3} value={notes} onChange={e => setNotes(e.target.value)}
            placeholder="Appears on the invoice"
            className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
            data-testid="invoice-notes-input" />
        </div>
        <div className="bg-white border border-slate-200 rounded-lg p-5">
          <label className="block text-xs font-medium text-slate-500 mb-1">
            Internal notes
          </label>
          <textarea rows={3} value={notesInternal} onChange={e => setInternal(e.target.value)}
            placeholder="Never shown to the customer"
            className="w-full border border-slate-200 rounded px-3 py-2 text-sm"
            data-testid="invoice-internal-notes-input" />
        </div>
      </div>
    </div>
  );
}
